import React, { Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';
import { getLocalItem, getSessionItem } from '../../utils/storage';

import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  PricingGrid,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  RelatedLinks,
  StructuredData,
  BrandTrustBand,
  RelatedTopServices,
  ProgrammaticLinkHub,
} from '../elements/premium';

import brandsData from '../../data/brands_data.json';
import { readInjectedLinkData, consumeInjectedLinkData, fetchLinkData } from '../../utils/linkHubData';
import brandContent from '../../data/brand_content.json';
import brandKeywordMap from '../../data/brand_keyword_map.json';
import keywordPages from '../../data/keyword_pages.json';
import { cities as citiesData } from '../elements/cities-data';
import { startingPriceLabel, getBikeServiceCards, getCarServiceCards, formatInr, PRICING } from '../../utils/pricing';
import { resolveBrandVehicleImage } from '../../utils/brandLeadModels';

// Merge bike + car arrays; shared brands (same slug) get merged models
function findBrand(slug) {
  const allBrands = [...(brandsData.bike || []), ...(brandsData.car || [])];
  const matches = allBrands.filter((b) => b.slug === slug);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  // Shared brand — merge
  return {
    ...matches[0],
    vehicleType: 'both',
    models: [...matches[0].models, ...matches[1].models]
      .filter((m, i, arr) => arr.findIndex((x) => x.slug === m.slug) === i),
    logo: matches[0].logo || matches[1].logo,
  };
}

function getOtherBrands(currentSlug, vehicleType) {
  let pool = [];
  if (vehicleType === 'car' || vehicleType === 'both') pool.push(...(brandsData.car || []));
  if (vehicleType === 'bike' || vehicleType === 'both') pool.push(...(brandsData.bike || []));
  // Deduplicate
  const seen = new Set();
  return pool
    .filter((b) => {
      if (b.slug === currentSlug || seen.has(b.slug)) return false;
      seen.add(b.slug);
      return true;
    })
    .slice(0, 12);
}

class BrandPage extends Component {
  constructor(props) {
    super(props);
    // Brand link-hub data is SSR-injected / fetched, never bundled.
    const injected = props.brandSlug ? readInjectedLinkData('brand', props.brandSlug) : null;
    this.state = { brandLinks: injected ? injected.data : null };
  }

  componentDidMount() {
    const { setOpenModal, isMobileApp, history } = this.props;
    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }
    if (isMobileApp) {
      history.push('/register');
    }
    this.loadBrandLinks(true);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.brandSlug !== this.props.brandSlug) this.loadBrandLinks(false);
  }

  loadBrandLinks(isInitial) {
    const { brandSlug } = this.props;
    if (!brandSlug) { this.setState({ brandLinks: null }); return; }
    const injected = consumeInjectedLinkData('brand', brandSlug);
    if (injected) { this.setState({ brandLinks: injected.data }); return; }
    if (isInitial && this.state.brandLinks) return;
    this.setState({ brandLinks: null });
    fetchLinkData(`/_data/links/brand/${brandSlug}`)
      .then((p) => this.setState({ brandLinks: p && p.data ? p.data : null }));
  }

  tryRequireLogo(logoFile) {
    if (!logoFile) return null;
    try {
      return require(`../../images/brand_logos/${logoFile}`);
    } catch (e) {
      return null;
    }
  }

  tryRequireImage(imagePath) {
    if (!imagePath) return null;
    try {
      return require(`../../images/vehicle_images/${imagePath}`);
    } catch (e) {
      return null;
    }
  }

  render() {
    const { brandSlug, isMobileBrowser, isMobileApp, user, setUser, setCitiesModal } = this.props;
    const brand = findBrand(brandSlug);

    if (!brand) return <Redirect to="/error-404" />;

    const content = brandContent[brandSlug] || {};
    const logo = this.tryRequireLogo(brand.logo);
    const canonical = `https://www.ridenrepair.com/brands/${brandSlug}`;

    const isBike = brand.vehicleType === 'bike';
    const isCar = brand.vehicleType === 'car';
    const startPrice = startingPriceLabel(isBike ? 'bike' : isCar ? 'car' : 'site');
    const vehicleWord = isBike ? 'Bike' : isCar ? 'Car' : 'Vehicle';

    // Transparent vehicle ghost in the hero — auto-picks a representative
    // (lead/flagship) model per brand from src/utils/brandLeadModels.js.
    // Falls back to the first model with a valid image; null if none.
    const heroVehicleImage = resolveBrandVehicleImage(brandSlug);

    // Related keywords
    const kwSlugs = brandKeywordMap[brandSlug] || [];
    const relatedKwLinks = kwSlugs
      .filter((s) => keywordPages[s])
      .slice(0, 8)
      .map((s) => ({
        title: keywordPages[s],
        href: `/${s}`,
        description: `Doorstep ${keywordPages[s].toLowerCase()} service with 30-day warranty.`,
      }));

    // Other brands
    const otherBrands = getOtherBrands(brandSlug, brand.vehicleType);

    // Pricing packages — sourced from pricing-snapshot (backend SoT). Never hardcode prices.
    const packages = isBike
      ? getBikeServiceCards()
      : isCar
        ? getCarServiceCards()
        : [
            { label: 'Bike Service', examples: 'From general service to repairs', price: formatInr(PRICING.marketing.bike_starting_price), features: ['All bike brands', 'Doorstep service', 'Genuine parts', '30-day warranty'], linkTo: '/service', btnLabel: 'Book Now' },
            { label: 'Car Service',  examples: 'From general service to AC repair', price: formatInr(PRICING.marketing.car_starting_price),  highlight: true, features: ['All car brands', 'Doorstep service', 'Genuine parts', '30-day warranty'], linkTo: '/service', btnLabel: 'Book Now' },
          ];

    // Schemas
    const schemas = [
      {
        '@type': 'ItemList',
        name: `${brand.label} Models`,
        numberOfItems: brand.models.length,
        itemListElement: brand.models.slice(0, 20).map((m, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: m.label,
          url: `https://www.ridenrepair.com/brands/${brandSlug}/${m.slug}`,
        })),
      },
    ];
    if (content.faqs && content.faqs.length) {
      schemas.push({
        '@type': 'FAQPage',
        mainEntity: content.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{content.metaTitle || `${brand.label} Service & Repair | Ride N Repair`}</title>
          <meta name="description" content={content.metaDescription || `Doorstep ${brand.label} service. Certified mechanics, genuine parts, 30-day warranty.`} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={content.metaTitle || `${brand.label} Service & Repair`} />
          <meta property="og:description" content={content.metaDescription || ''} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        <Header isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} user={user} setUser={setUser} />

        <PremiumHero
          compact
          badge={`${brand.label} Service`}
          title={`${brand.label} ${vehicleWord} Service & Repair`}
          priceTag={`Starting ${startPrice}`}
          subtitle={`Expert doorstep service for all ${brand.label} models. Certified mechanics, genuine parts, 30-day warranty.`}
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          vehicleImage={heroVehicleImage}
          vehicleImageAlt={`${brand.label} vehicle`}
          ctas={[
            { label: `Book ${brand.label} Service`, href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
          trustItems={[
            { value: '2,00,000+', label: 'Customers Served' },
            { value: '4.8\u2605', label: 'Customer Rating' },
            { value: '32+', label: 'Cities in India' },
            { value: '30-Day', label: 'Service Warranty' },
          ]}
        />

        {/* Trust signals already render inside PremiumHero trustItems above —
            no second BrandTrustBand here to avoid duplicate visual band. */}

        <Breadcrumbs items={[
          { label: 'Home', href: '/' },
          { label: 'Brands', href: '/brands' },
          { label: brand.label },
        ]} />

        <StickyMobileCTA title={`${brand.label} Service`} priceHint={`Starting ${startPrice} · 30-Day Warranty`} btnLabel="Book Now" btnHref="/service" />

        {/* BRAND INTRO */}
        {content.intro && (
          <SectionWrapper kicker="About" heading={`${brand.label} Service`}>
            <div className="rr-brand-hero-card">
              {logo && (
                <img
                  src={typeof logo === 'object' ? logo.default : logo}
                  alt={`${brand.label} logo`}
                  className="rr-brand-hero-card__logo"
                />
              )}
              <div className="rr-brand-hero-card__text">
                <h3>{brand.label} Doorstep Service</h3>
                <p>{content.intro}</p>
              </div>
            </div>
          </SectionWrapper>
        )}

        {/* MODELS GRID */}
        <SectionWrapper variant="gray" kicker="Models" heading={`${brand.label} Models We Service (${brand.models.length})`}>
          <div className="rr-model-grid">
            {brand.models.map((model) => {
              const img = this.tryRequireImage(model.imagePath);
              return (
                <Link key={model.slug} to={`/brands/${brandSlug}/${model.slug}`} className="rr-model-card">
                  {img ? (
                    <img
                      src={typeof img === 'object' ? img.default : img}
                      alt={model.label}
                      className="rr-model-card__img"
                      loading="lazy"
                    />
                  ) : (
                    <div className="rr-model-card__placeholder">{isBike ? '🏍️' : '🚗'}</div>
                  )}
                  <span className="rr-model-card__name">{model.label}</span>
                  <span className="rr-model-card__cta">View Services &rarr;</span>
                </Link>
              );
            })}
          </div>
        </SectionWrapper>

        {/* PRICING */}
        <SectionWrapper variant="dark" kicker="Transparent Pricing" heading={`${brand.label} Service Pricing`}>
          <PricingGrid packages={packages} />
        </SectionWrapper>

        {/* RELATED KEYWORDS */}
        {relatedKwLinks.length > 0 && (
          <SectionWrapper variant="gray" kicker="Related Services" heading={`${brand.label} Services`}>
            <RelatedLinks links={relatedKwLinks} />
          </SectionWrapper>
        )}

        {/* CITIES */}
        <SectionWrapper kicker="Available In" heading={`${brand.label} Service — Cities`}>
          <div className="rr-city-grid">
            {(citiesData || []).map((city) => (
              <Link key={city.key} to={`/city/${city.key}`} className="rr-city-chip">
                {city.name}
              </Link>
            ))}
          </div>
        </SectionWrapper>

        {/* DOORSTEP SERVICE LINK HUB — every brand×service×city leaf for this
            brand, so the programmatic pages are reachable in ≤3 clicks. */}
        {this.state.brandLinks && this.state.brandLinks.length > 0 && (
          <ProgrammaticLinkHub
            heading={`${brand.label} Doorstep Service Across India`}
            subheading={`Book ${brand.label} service, repair and more at your doorstep — pick your city.`}
            links={this.state.brandLinks}
          />
        )}

        {/* FAQS */}
        {content.faqs && content.faqs.length > 0 && (
          <SectionWrapper narrow variant="gray" kicker="FAQ" heading={`${brand.label} Service — Common Questions`}>
            <FaqAccordion faqs={content.faqs} withSchema={false} />
          </SectionWrapper>
        )}

        {/* OTHER BRANDS */}
        <SectionWrapper kicker="More Brands" heading="Other Brands We Service">
          <div className="rr-brand-grid">
            {otherBrands.map((b) => {
              const bLogo = this.tryRequireLogo(b.logo);
              return (
                <Link key={b.slug} to={`/brands/${b.slug}`} className="rr-brand-card">
                  {bLogo ? (
                    <img
                      src={typeof bLogo === 'object' ? bLogo.default : bLogo}
                      alt={`${b.label} logo`}
                      className="rr-brand-card__logo"
                      loading="lazy"
                    />
                  ) : (
                    <div className="rr-brand-card__logo" style={{ fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {b.label.charAt(0)}
                    </div>
                  )}
                  <span className="rr-brand-card__label">{b.label}</span>
                  <span className="rr-brand-card__count">{b.models.length} models</span>
                </Link>
              );
            })}
          </div>
          <div className="rr-text-center rr-mt-32">
            <Link to="/brands" className="rr-cta-dark-outline">View All Brands</Link>
          </div>
        </SectionWrapper>

        {/* RELATED — booking-driven city links from this brand page */}
        <RelatedTopServices variant="brand" kicker="Where We Serve" heading={`${brand.label} Doorstep Service in Top Cities`} />

        <FinalCTA
          heading={<>Need {brand.label} Service?<br />Book Now — Get It Done Today.</>}
          subtitle={`Doorstep service · Certified mechanics · ${startPrice} onwards · 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer isMobileBrowser={isMobileBrowser} isMobileApp={isMobileApp} setCitiesModal={setCitiesModal} />
      </div>
    );
  }
}

export default BrandPage;
